'use client'

import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { type ArticleReportType } from '@/defs/articleReport'
import { type Dict } from '@/defs/misc'

import { parseContent, type ParseContentProps } from './parseContent'

export type ParsedContentContextType = {
  content: ReactNode
  reports?: ArticleReportType[]
  sections?: Dict<ReactNode>
}

const ParsedContentContext = createContext<ParsedContentContextType | null>(null)

type Props = {
  children: ReactNode
  sections?: {
    content: string
    slug: string
  }[]
} & ParseContentProps

export const ParsedContentProvider = ({ articleSlug, articleTitle, children, content, getReport, sections }: Props) => {
  const value = useMemo(() => {
    const parsedContent = parseContent({ articleSlug, articleTitle, content, getReport })

    if (!sections?.length) return parsedContent

    const output: ParsedContentContextType = {
      ...parsedContent,
    }

    sections.forEach(({ content, slug }) => {
      const parsedContent = parseContent({ articleSlug, articleTitle, content, getReport })
      if (!output.sections) output.sections = {}
      output.sections[slug] = parsedContent.content
      if (parsedContent.reports?.length) {
        if (!output.reports) output.reports = []
        output.reports.push(...parsedContent.reports)
      }
    })

    return output
  }, [content, getReport, sections, articleTitle, articleSlug])

  return <ParsedContentContext.Provider value={value}>{children}</ParsedContentContext.Provider>
}

export const useParsedContent = (): ParsedContentContextType => {
  const parsedContent = useContext(ParsedContentContext)

  if (!parsedContent) throw new Error('useParsedContent must be used within ParsedContentProvider')

  return parsedContent
}
